import { useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border border-line bg-white px-4 py-3 text-sm text-ink outline-none transition-colors duration-200 placeholder:text-mute/50 focus:border-brand";

export default function EnquiryForm({ kind, testid, showOrderId = false, showCity = false, buttonLabel = "Send message" }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", order_id: "", city: "", message: "" });
  const [state, setState] = useState({ loading: false, ticket: null, error: null });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setState({ loading: true, ticket: null, error: null });
    try {
      const { data } = await axios.post(`${API}/enquiries`, { kind, ...form });
      setState({ loading: false, ticket: data.ticket_id, error: null });
    } catch (err) {
      const msg = err?.response?.data?.detail;
      setState({ loading: false, ticket: null, error: typeof msg === "string" ? msg : "Something went wrong. Please try again or email us directly." });
    }
  };

  if (state.ticket) {
    return (
      <div className="border border-line bg-white p-8 md:p-10" data-testid={`${testid}-success`}>
        <CheckCircle2 className="h-10 w-10 text-brand" strokeWidth={1.6} />
        <h3 className="mt-5 font-serif text-2xl font-semibold">Received. Thank you.</h3>
        <p className="mt-3 text-sm leading-relaxed text-mute">
          Your reference number is{" "}
          <span className="font-mono font-semibold text-ink" data-testid={`${testid}-ticket`}>{state.ticket}</span>.
          Our team will acknowledge within 48 hours. Keep this number for any follow-up at{" "}
          <a href="mailto:info.touristjunction@gmail.com" className="font-semibold text-brand">info.touristjunction@gmail.com</a>.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="border border-line bg-white p-6 md:p-10" data-testid={`${testid}-form`}>
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-mute" htmlFor={`${testid}-name`}>Full name *</label>
          <input id={`${testid}-name`} data-testid={`${testid}-name-input`} required minLength={2} className={inputCls} value={form.name} onChange={set("name")} placeholder="Your name" />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-mute" htmlFor={`${testid}-email`}>Email *</label>
          <input id={`${testid}-email`} data-testid={`${testid}-email-input`} required type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@example.com" />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-mute" htmlFor={`${testid}-phone`}>Phone</label>
          <input id={`${testid}-phone`} data-testid={`${testid}-phone-input`} className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+91" />
        </div>
        {showCity ? (
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-mute" htmlFor={`${testid}-city`}>City / Town</label>
            <input id={`${testid}-city`} data-testid={`${testid}-city-input`} className={inputCls} value={form.city} onChange={set("city")} placeholder="Where would you work?" />
          </div>
        ) : (
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-mute" htmlFor={`${testid}-subject`}>Subject</label>
            <input id={`${testid}-subject`} data-testid={`${testid}-subject-input`} className={inputCls} value={form.subject} onChange={set("subject")} placeholder="How can we help?" />
          </div>
        )}
        {showOrderId && (
          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-mute" htmlFor={`${testid}-order`}>Order / Booking ID (if any)</label>
            <input id={`${testid}-order`} data-testid={`${testid}-order-input`} className={inputCls} value={form.order_id} onChange={set("order_id")} placeholder="e.g. from your in-app job screen" />
          </div>
        )}
        <div className="sm:col-span-2">
          <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-mute" htmlFor={`${testid}-message`}>Message *</label>
          <textarea id={`${testid}-message`} data-testid={`${testid}-message-input`} required minLength={10} rows={5} className={inputCls} value={form.message} onChange={set("message")} placeholder="Write the details here (minimum 10 characters)" />
        </div>
      </div>
      {state.error && (
        <p className="mt-4 border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid={`${testid}-error`}>{state.error}</p>
      )}
      <button
        type="submit"
        data-testid={`${testid}-submit-btn`}
        disabled={state.loading}
        className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand px-8 py-3.5 text-sm font-bold text-white transition-colors duration-300 hover:bg-brand-dark disabled:opacity-60"
      >
        {state.loading && <Loader2 className="h-4 w-4 animate-spin" />}
        {buttonLabel}
      </button>
    </form>
  );
}
